/**
 * Inline Appointment Booking Step for Nimbus-Guided Intakes
 *
 * Appears BEFORE payment in the intake flow for Nimbus-fulfilled guided
 * orders. The patient picks a date and time slot — the slot is held for 5
 * minutes — then advances to payment. The actual appointment is created
 * server-side as part of the bundled submit (intake + appointment) on
 * payment success.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { CalendarPicker } from '@/components/booking/CalendarPicker';
import { TimeSlotGrid } from '@/components/booking/TimeSlotButton';
import {
  Calendar,
  Clock,
  Globe,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react';

const API_BASE_URL = (
  (import.meta.env.VITE_API_BASE_URL as string | undefined)
  || 'http://localhost:3001/api'
).replace(/\/+$/, '');

interface TimeSlot {
  start: string;
  end: string;
  local_start?: string;
  local_end?: string;
}

export interface SelectedSlot {
  slot_start: string;
  slot_end: string;
  timezone: string;
}

export interface IntakeBookingStepProps {
  token: string;
  /** Called when patient picks a slot and clicks Continue. */
  onSlotSelected: (slot: SelectedSlot) => void;
  /** Called when patient clicks Back — parent should return to the previous step. */
  onBack: () => void;
  /** Pre-existing selection so the step preserves state on back-navigation. */
  initialSlot?: SelectedSlot | null;
}

function formatSlotDisplayTime(slot: TimeSlot, tz: string): string {
  const dt = slot.local_start || slot.start;
  try {
    const d = new Date(dt);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: tz,
    });
  } catch {
    return dt;
  }
}

async function fetchDates(
  token: string,
  year: number,
  month: number,
  timezone: string,
): Promise<string[]> {
  const res = await fetch(
    `${API_BASE_URL}/intakes/booking/${token}/dates?year=${year}&month=${month}&timezone=${encodeURIComponent(timezone)}`,
  );
  const data = await res.json();
  if (!data.success) return [];
  return (data.data.dates as Array<{ date: string; hasSlots: boolean }>)
    .filter((d) => d.hasSlots)
    .map((d) => d.date);
}

async function fetchSlots(
  token: string,
  date: string,
  timezone: string,
): Promise<{ slots: TimeSlot[]; duration: number }> {
  const res = await fetch(
    `${API_BASE_URL}/intakes/booking/${token}/slots?date=${date}&timezone=${encodeURIComponent(timezone)}`,
  );
  const data = await res.json();
  if (!data.success) return { slots: [], duration: 30 };
  return { slots: data.data.slots, duration: data.data.duration };
}

async function holdSlotApi(
  token: string,
  slotStart: string,
  slotEnd: string,
  timezone: string,
): Promise<{ success: boolean; hold_expires_at?: string; error?: string }> {
  const res = await fetch(`${API_BASE_URL}/intakes/booking/${token}/hold`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slot_start: slotStart, slot_end: slotEnd, timezone }),
  });
  const data = await res.json();
  return {
    success: data.success,
    hold_expires_at: data.data?.hold_expires_at,
    error: data.error,
  };
}

export default function IntakeBookingStep({
  token,
  onSlotSelected,
  onBack,
  initialSlot,
}: IntakeBookingStepProps) {
  const [timezone] = useState(
    () => initialSlot?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (initialSlot?.slot_start) return new Date(initialSlot.slot_start);
    return new Date();
  });
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [loadingDates, setLoadingDates] = useState(true);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    initialSlot?.slot_start ? new Date(initialSlot.slot_start) : undefined,
  );
  const [rawSlots, setRawSlots] = useState<TimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [selectedSlotStart, setSelectedSlotStart] = useState<string | undefined>(
    initialSlot?.slot_start,
  );
  const [selectedSlotEnd, setSelectedSlotEnd] = useState<string | undefined>(
    initialSlot?.slot_end,
  );

  const [holdExpiresAt, setHoldExpiresAt] = useState<Date | null>(null);
  const [holdCountdown, setHoldCountdown] = useState<number | null>(null);
  const holdTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [error, setError] = useState<string | null>(null);

  const slotsRef = useRef<HTMLDivElement>(null);

  // Auto-scroll the time-slots block into view once it renders for the picked
  // date — otherwise it falls below the fold on most laptop viewports.
  useEffect(() => {
    if (selectedDate && rawSlots.length > 0) {
      const t = setTimeout(() => {
        slotsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
      return () => clearTimeout(t);
    }
  }, [selectedDate, rawSlots.length]);

  // Fetch available dates when month or timezone changes
  useEffect(() => {
    let cancelled = false;
    setLoadingDates(true);
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;

    fetchDates(token, year, month, timezone).then((dates) => {
      if (!cancelled) {
        setAvailableDates(dates);
        setLoadingDates(false);
      }
    }).catch(() => {
      if (!cancelled) setLoadingDates(false);
    });

    return () => { cancelled = true; };
  }, [token, currentMonth, timezone]);

  // Fetch slots when date changes
  useEffect(() => {
    if (!selectedDate) {
      setRawSlots([]);
      return;
    }

    let cancelled = false;
    setLoadingSlots(true);
    setError(null);

    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    fetchSlots(token, dateStr, timezone).then(({ slots }) => {
      if (!cancelled) {
        setRawSlots(slots);
        setLoadingSlots(false);
      }
    }).catch(() => {
      if (!cancelled) setLoadingSlots(false);
    });

    return () => { cancelled = true; };
  }, [token, selectedDate, timezone]);

  // Hold countdown timer
  useEffect(() => {
    if (holdTimerRef.current) clearInterval(holdTimerRef.current);
    if (!holdExpiresAt) {
      setHoldCountdown(null);
      return;
    }

    const tick = () => {
      const remaining = Math.max(
        0,
        Math.floor((holdExpiresAt.getTime() - Date.now()) / 1000),
      );
      setHoldCountdown(remaining);
      if (remaining <= 0) {
        if (holdTimerRef.current) clearInterval(holdTimerRef.current);
        setHoldExpiresAt(null);
        setSelectedSlotStart(undefined);
        setSelectedSlotEnd(undefined);
        setError('Your hold expired. Please select a new time slot.');
      }
    };
    tick();
    holdTimerRef.current = setInterval(tick, 1000);

    return () => {
      if (holdTimerRef.current) clearInterval(holdTimerRef.current);
    };
  }, [holdExpiresAt]);

  const displaySlots = rawSlots.map((s) => ({
    start: s.start,
    end: s.end,
    displayTime: formatSlotDisplayTime(s, timezone),
  }));

  const handleSelectSlot = useCallback(
    async (slotStart: string) => {
      const slot = rawSlots.find((s) => s.start === slotStart);
      if (!slot) return;

      setSelectedSlotStart(slotStart);
      setSelectedSlotEnd(slot.end);
      setError(null);

      try {
        const result = await holdSlotApi(token, slot.start, slot.end, timezone);
        if (result.success && result.hold_expires_at) {
          setHoldExpiresAt(new Date(result.hold_expires_at));
        } else if (result.error === 'SLOT_HELD') {
          setError('This slot was just taken. Please choose another.');
          setSelectedSlotStart(undefined);
          setSelectedSlotEnd(undefined);
        }
      } catch {
        // Non-fatal — patient can still try to continue, submit will re-check
      }
    },
    [token, rawSlots, timezone],
  );

  const handleContinue = () => {
    if (!selectedSlotStart || !selectedSlotEnd) return;
    onSlotSelected({
      slot_start: selectedSlotStart,
      slot_end: selectedSlotEnd,
      timezone,
    });
  };

  const formatHoldCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div
          className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
          style={{ background: 'rgba(74, 138, 154, 0.15)' }}
        >
          <Calendar className="w-7 h-7 text-[#4A8A9A]" />
        </div>
        <h3 className="text-xl font-semibold text-white font-montserrat">
          Book Your Appointment
        </h3>
        <p className="text-white/50 text-sm max-w-md mx-auto">
          Schedule your telehealth visit with your provider. The slot is held
          while you complete payment.
        </p>
      </div>

      {/* Timezone info */}
      <div className="flex items-center justify-center gap-2 text-xs text-white/40">
        <Globe className="w-3.5 h-3.5" />
        <span>Times shown in {timezone.replace(/_/g, ' ')}</span>
      </div>

      {/* Calendar — wrapped in light theme container for CalendarPicker compatibility */}
      <div className="rounded-xl p-4 bg-white">
        <CalendarPicker
          selectedDate={selectedDate}
          onSelectDate={(d) => setSelectedDate(d)}
          availableDates={availableDates}
          currentMonth={currentMonth}
          onMonthChange={setCurrentMonth}
          loading={loadingDates}
        />
      </div>

      {/* Time slots */}
      {selectedDate && (
        <div ref={slotsRef} className="space-y-3 scroll-mt-24">
          <div className="flex items-center gap-2 text-white/70 text-sm">
            <Clock className="w-4 h-4" />
            <span>
              Available times for{' '}
              <span className="text-white font-medium">
                {format(selectedDate, 'EEEE, MMMM d')}
              </span>
            </span>
          </div>
          <div className="rounded-xl p-4 bg-white">
            <TimeSlotGrid
              slots={displaySlots}
              selectedSlot={selectedSlotStart}
              onSelectSlot={handleSelectSlot}
              loading={loadingSlots}
              emptyMessage="No available times for this date"
            />
          </div>
        </div>
      )}

      {/* Hold countdown */}
      {holdCountdown !== null && holdCountdown > 0 && (
        <div
          className="flex items-center justify-center gap-2 text-sm rounded-lg px-4 py-2"
          style={{
            background: 'rgba(74, 138, 154, 0.1)',
            border: '1px solid rgba(74, 138, 154, 0.3)',
            color: '#4A8A9A',
          }}
        >
          <Clock className="w-4 h-4" />
          <span>
            Slot held for{' '}
            <span className="font-semibold">
              {formatHoldCountdown(holdCountdown)}
            </span>{' '}
            — complete payment to confirm
          </span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 text-sm rounded-lg px-4 py-3 bg-red-500/10 border border-red-500/30 text-red-400">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Actions */}
      <div
        className="flex justify-between pt-6"
        style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}
      >
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 rounded-lg text-white transition-colors"
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          type="button"
          disabled={!selectedSlotStart}
          onClick={handleContinue}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed"
          style={
            selectedSlotStart
              ? {
                  background: 'linear-gradient(90deg, #3B82F6, #2FFFC7)',
                  color: '#000',
                }
              : {
                  background: 'rgb(71, 85, 105)',
                  color: 'rgb(148, 163, 184)',
                }
          }
        >
          Continue to Payment
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
